import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentAPI, restaurantAPI } from '../../services/api';
import { load } from '@cashfreepayments/cashfree-js';

function SubscriptionPayment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [restaurantData, setRestaurantData] = useState(null);

  useEffect(() => {
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    try {
      const response = await restaurantAPI.getSubscriptionPlanStatus();
      const data = response.data.data;

      // If plan already selected, redirect to dashboard
      if (data.subscriptionPlan && data.subscriptionPlan.type !== 'none') {
        navigate('/restaurant/dashboard');
        return;
      }

      // Check if restaurant is approved
      if (!data.isApproved || data.approvalStatus !== 'approved') {
        navigate('/restaurant/onboarding');
        return;
      }

      setRestaurantData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error checking eligibility:', error);
      setError('Failed to verify eligibility');
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaymentProcessing(true);
    setError('');

    try {
      // Create payment session
      const orderResponse = await paymentAPI.createSubscriptionPayment({
        amount: 10000,
        planType: 'one_time'
      });

      const { paymentSessionId, orderToken, cashfreeOrderId } = orderResponse.data.data;

      // Initialize Cashfree
      const cashfree = await load({
        mode: process.env.REACT_APP_CASHFREE_ENV === 'PROD' ? 'production' : 'sandbox'
      });

      // Prepare checkout options
      const checkoutOptions = {
        paymentSessionId: paymentSessionId,
        redirectTarget: '_self'
      };

      // Open Cashfree checkout
      // After payment, Cashfree will redirect to /restaurant/payment-verification
      cashfree.checkout(checkoutOptions);
    } catch (error) {
      console.error('Payment initiation failed:', error);
      setError(error.response?.data?.message || 'Failed to initiate payment');
      setPaymentProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F8FAFC'
      }}>
        <div style={{
          color: '#64748B',
          fontSize: '16px',
          fontWeight: '500'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F8FAFC',
      padding: '24px 16px'
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
            borderRadius: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 4px 12px rgba(255, 105, 97, 0.2)'
          }}>
            <span style={{ fontSize: '40px' }}>💳</span>
          </div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#0F172A',
            marginBottom: '8px',
            margin: 0
          }}>
            Complete Payment
          </h1>
          <p style={{
            color: '#64748B',
            fontSize: '15px',
            margin: '8px 0 0 0'
          }}>
            One-time subscription - Initial payment
          </p>
        </div>

        {error && (
          <div style={{
            background: '#FEE2E2',
            border: '1px solid #FCA5A5',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            color: '#DC2626',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Order Summary Card */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '16px',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
          border: '1px solid #F1F5F9'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#0F172A',
            marginBottom: '20px',
            margin: 0,
            paddingBottom: '16px',
            borderBottom: '1px solid #F1F5F9'
          }}>
            Order Summary
          </h3>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{
                color: '#64748B',
                fontSize: '14px'
              }}>
                Plan Type
              </span>
              <span style={{
                fontWeight: '600',
                color: '#0F172A',
                fontSize: '14px'
              }}>
                One-Time Fee
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{
                color: '#64748B',
                fontSize: '14px'
              }}>
                Total Amount
              </span>
              <span style={{
                fontWeight: '600',
                color: '#0F172A',
                fontSize: '14px'
              }}>
                ₹50,000
              </span>
            </div>

            <div style={{
              background: '#FFF7ED',
              borderRadius: '12px',
              padding: '16px',
              marginTop: '8px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <span style={{
                  color: '#0F172A',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Due Now
                </span>
                <span style={{
                  fontWeight: '700',
                  fontSize: '24px',
                  color: '#FF6961'
                }}>
                  ₹10,000
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{
                  color: '#64748B',
                  fontSize: '13px'
                }}>
                  After 2-Month Trial
                </span>
                <span style={{
                  fontWeight: '600',
                  color: '#64748B',
                  fontSize: '14px'
                }}>
                  ₹40,000
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* What's Included Card */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
          border: '1px solid #F1F5F9'
        }}>
          <h4 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#0F172A',
            marginBottom: '16px',
            margin: 0
          }}>
            What's Included
          </h4>
          <ul style={{
            margin: 0,
            padding: 0,
            listStyle: 'none'
          }}>
            {[
              'Unlimited orders for lifetime',
              'Zero commission on orders',
              '2-month trial period',
              'Remaining payment after trial',
              'Full platform access immediately'
            ].map((item, index) => (
              <li key={index} style={{
                color: '#64748B',
                fontSize: '14px',
                lineHeight: '1.6',
                marginBottom: '12px',
                paddingLeft: '24px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  color: '#10B981',
                  fontWeight: '600'
                }}>✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Payment Button */}
        <button
          onClick={handlePayment}
          disabled={paymentProcessing}
          style={{
            width: '100%',
            padding: '18px',
            fontSize: '16px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '16px',
            background: paymentProcessing
              ? '#CBD5E1'
              : 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            cursor: paymentProcessing ? 'not-allowed' : 'pointer',
            boxShadow: paymentProcessing
              ? 'none'
              : '0 4px 12px rgba(255, 105, 97, 0.3)',
            transition: 'all 0.3s ease',
            opacity: paymentProcessing ? 0.6 : 1
          }}
        >
          {paymentProcessing ? (
            <>
              <span>🔄</span>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>💳</span>
              <span>Pay ₹10,000 Now</span>
            </>
          )}
        </button>

        {/* Back Link */}
        <div style={{
          textAlign: 'center',
          marginBottom: '24px'
        }}>
          <button
            onClick={() => navigate('/restaurant/plan-selection')}
            disabled={paymentProcessing}
            style={{
              background: 'none',
              border: 'none',
              color: '#FF6961',
              fontSize: '14px',
              fontWeight: '600',
              cursor: paymentProcessing ? 'not-allowed' : 'pointer',
              opacity: paymentProcessing ? 0.5 : 1
            }}
          >
            ← Back to Plan Selection
          </button>
        </div>

        {/* Security Badges */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
          border: '1px solid #F1F5F9'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              🔒
            </div>
            <div style={{ flex: 1 }}>
              <p style={{
                margin: 0,
                color: '#0F172A',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '4px'
              }}>
                Secure Payment
              </p>
              <p style={{
                margin: 0,
                color: '#64748B',
                fontSize: '12px',
                lineHeight: '1.5'
              }}>
                Your payment is encrypted and secure
              </p>
            </div>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            paddingTop: '12px',
            borderTop: '1px solid #F1F5F9'
          }}>
            <span style={{
              color: '#64748B',
              fontSize: '12px'
            }}>
              Powered by
            </span>
            <span style={{
              color: '#0F172A',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              Cashfree
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPayment;
